import globals from "@lvt/globals";
import {
    Button,
    TextField,
    IconButton,
    Container,
    Grid,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    DialogActions,
    makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { Skeleton } from "@material-ui/lab";
import { useState } from "react";

import { useAddPresetMutation } from "@/api/liveUnitMosaic/liveUnitMosaic";
import { useSelector } from "@/store";


const useStyles = makeStyles((theme) => ({
    inputLabel: {
        color: theme.palette.primary.contrastText,
    },
    input: {
        color: theme.palette.primary.contrastText,
        "&::before": {
            borderBottomColor: theme.palette.secondary.main,
        },
        "&::after": {
            borderBottomColor: theme.palette.secondary.main,
        },
        "&:hover": {
            color: theme.palette.primary.contrastText,
            "&::before": {
                borderBottomColor: theme.palette.secondary.main,
            },
            "&::after": {
                borderBottomColor: theme.palette.secondary.main,
            },
        },
    },
    container: {
        height: 64,
        margin: "8px 0",
    },
    extraMargin: {
        marginTop: 12,
    },
    dialogTitle: {
        color: theme.palette.secondary.main,
    },
    dialog: {
        backgroundColor: theme.palette.primary.main,
    },
    linkText: {
        color: theme.palette.secondary.main,
    },
    dialogText: {
        color: theme.palette.primary.contrastText,
    },
    addButton: {
        marginTop: 8,
    },
}));

export interface AddPresetProps {
    liveUnitId: number;
}

const AddPreset: React.FC<AddPresetProps> = ({ liveUnitId }) => {
    const styles = useStyles();

    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const cameraId = useSelector((state) => state.cameras.streamingId);

    const [addPreset] = useAddPresetMutation();

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();

        if (newName && typeof newName === "string" && cameraId) {
            setSaving(true);

            addPreset({
                liveUnitId,
                cameraId,
                name: newName,
            }).then(() => {
                setAdding(false);
                setNewName("");
                setAlertOpen(true);
                setSaving(false);
            });
        }
    };

    return (
        <Container classes={{ root: styles.container + (adding ? " " + styles.extraMargin : "") }}>
            {adding ? (
                <form onSubmit={handleSubmit} id="addPresetForm">
                    <Grid container spacing={1}>
                        <Grid item xs={9}>
                            <TextField
                                label="New Preset"
                                InputLabelProps={{ classes: { root: styles.inputLabel } }}
                                InputProps={{ classes: { root: styles.input } }}
                                color="secondary"
                                inputProps={{
                                    "aria-label": "new-preset",
                                }}
                                variant="filled"
                                autoFocus
                                required
                                name="presetName"
                                onChange={(e) => setNewName(e.target.value)}
                                value={newName}
                                fullWidth
                            />
                        </Grid>
                        <Grid item container xs={3} spacing={1}>
                            <Grid item xs={6}>
                                {saving ? (
                                    <Skeleton>
                                        <IconButton>
                                            <CloseIcon />
                                        </IconButton>
                                    </Skeleton>
                                ) : (
                                    <IconButton
                                        aria-label="cancel-add-preset"
                                        onClick={() => {
                                            setNewName("");
                                            setAdding(false);
                                        }}
                                    >
                                        <CloseIcon color="error" />
                                    </IconButton>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                {saving ? (
                                    <Skeleton>
                                        <IconButton>
                                            <CheckIcon />
                                        </IconButton>
                                    </Skeleton>
                                ) : (
                                    <IconButton aria-label="save-preset" type="submit" form="addPresetForm">
                                        <CheckIcon color="secondary" />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            ) : (
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    aria-label="add-preset"
                    onClick={() => setAdding(true)}
                    size="large"
                    classes={{ root: styles.addButton }}
                >
                    New Preset
                </Button>
            )}
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)} classes={{ paper: styles.dialog }}>
                <DialogTitle classes={{ root: styles.dialogTitle }}>Additional action may be required</DialogTitle>
                <DialogContent>
                    <DialogContentText classes={{ root: styles.dialogText }}>
                        Your preset has been added, but may require additional configuration. To enable patrol,
                        archiving, and other features for this preset, please adjust the settings for this camera&nbsp;
                        <a href={`${globals.servicePortal.url}/camera/${cameraId}`} className={styles.linkText}>
                            HERE
                        </a>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" size="large" variant="outlined" onClick={() => setAlertOpen(false)}>
                        I Understand
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};
export default AddPreset;
